import { type RpmPicks, RpmType } from '@on3/api';
import { On3IconCheck, On3IconFlippedPrediction, On3IconX } from '@on3/icons';
import { useAuth } from '@on3/ui-lib/index';
import { slugify } from '@on3/ui-lib/utils/slugify';
import clsx from 'clsx';
import { RpmBarrier, RpmUtmCampaigns } from 'components/Barriers/RpmBarrier';
import { On3Avatar } from 'components/Image/On3Avatar';
import { TrendArrow } from 'components/TrendArrow/TrendArrow';
import { TextLink } from 'components/UI/Link/TextLink';
import { Text } from 'components/UI/Typography/Text';
import { format } from 'date-fns';
import { useMemo } from 'react';

import { ConfidenceBar } from './ConfidenceBar';
import styles from './InsiderPredictions.module.scss';

const changeLabel = {
  ConfidenceChange: 'Change in confidence level',
  NewLeader: 'New leader',
  Pick: '',
  PercentChange: 'Change in rpm',
};

interface IInsiderPredictionsItemProps extends RpmPicks {
  sport?: string | null;
}
export const InsiderPredictionsItem = ({
  correct,
  organization,
  dateAdded,
  expert,
  confidence,
  previousConfidence,
  previousDateAdded,
  flippedFromOrganization,
  articleLink,
  topTeams,
  premium,
  year,
  type,
  sport,
}: IInsiderPredictionsItemProps) => {
  const { name } = expert || {};
  const { user } = useAuth();
  const showFlippedOrg = flippedFromOrganization?.slug;
  const showOrg = organization?.slug;
  const isPick = type === RpmType.Pick;
  const isConfidenceChange = type === RpmType.ConfidenceChange;
  const isNewLeader = type === RpmType.NewLeader;
  const isRankChange = type === RpmType.PercentChange;
  const isPickFinal = correct != null && !isNewLeader && !isRankChange;
  const showRpmBarrier = isPick && !isPickFinal && !user?.has && premium;
  const newLeader = topTeams?.[0];
  const oldLeader = topTeams?.[1];
  const hasConfidence = confidence !== null && confidence !== undefined;
  const hasPreviousConfidence =
    previousConfidence !== null &&
    previousConfidence !== undefined &&
    isConfidenceChange;
  const sluggedSport = slugify(sport || 'football');
  const predictionDate = useMemo(() => {
    if (dateAdded) {
      const newLocal = format(new Date(dateAdded), 'M/dd/yy hh:mm a');
      const [date, ...time] = newLocal.split(' ');

      return `${date}\n${time.join('\xa0')}`;
    }

    return '-';
  }, [dateAdded]);
  const previousDate = useMemo(() => {
    if (previousDateAdded) {
      const newLocal = format(new Date(previousDateAdded), 'M/dd/yy hh:mm a');
      const [date, ...time] = newLocal.split(' ');

      return `${date}\n${time.join('\xa0')}`;
    }

    return '-';
  }, [previousDateAdded]);

  const rankChange: keyof typeof styles = useMemo(() => {
    if (newLeader?.currentPercentage && newLeader.previousPercentage) {
      return newLeader?.previousPercentage > newLeader?.currentPercentage
        ? 'negative'
        : 'positive';
    }

    return 'neutral';
  }, [newLeader?.currentPercentage, newLeader?.previousPercentage]);

  const typeOfChangeLabel = type && changeLabel[type];

  const typeOfChangeValue: keyof typeof styles = useMemo(() => {
    if (hasPreviousConfidence && hasConfidence) {
      return previousConfidence > confidence ? 'negative' : 'positive';
    }

    return 'neutral';
  }, [confidence, hasConfidence, hasPreviousConfidence, previousConfidence]);

  return (
    <li
      className={clsx(styles.predicitionItem, {
        [styles.hasLevelChange]: isConfidenceChange,
        [styles.hasRankChange]: isNewLeader || isRankChange,
      })}
    >
      {typeOfChangeLabel && (
        <Text className={styles.typeOfChange} variant="subtitle1">
          {typeOfChangeLabel}
        </Text>
      )}
      {(isPick || isConfidenceChange) && !showRpmBarrier && (
        <div className={styles.predictionWrapper}>
          {organization?.asset && (
            <div
              className={clsx(styles.logosContainer, {
                [styles.isFlipped]: showFlippedOrg,
              })}
            >
              <div className={styles.flippedPickIcons}>
                {showFlippedOrg && (
                  <>
                    <TextLink
                      className={styles.teamLogoLink}
                      href={`/college/${flippedFromOrganization.slug}/${sluggedSport}/${year}/industry-comparison-commits/`}
                      underline="none"
                    >
                      <On3Avatar
                        className={clsx(
                          styles.teamLogo,
                          styles.flippedFromLogo,
                        )}
                        domain={flippedFromOrganization?.asset?.domain}
                        height={30}
                        source={flippedFromOrganization?.asset?.source}
                        teamAvatar
                        type={flippedFromOrganization?.asset?.type}
                        width={30}
                      />
                    </TextLink>
                    <On3IconFlippedPrediction
                      className={styles.predictionSwappedIcon}
                    />
                  </>
                )}
                {showOrg && (
                  <TextLink
                    className={styles.teamLogoLink}
                    href={`/college/${organization.slug}/${sluggedSport}/${year}/industry-comparison-commits/`}
                    underline="none"
                  >
                    <On3Avatar
                      className={styles.teamLogo}
                      domain={organization?.asset?.domain}
                      height={50}
                      source={organization?.asset?.source}
                      teamAvatar
                      type={organization?.asset?.type}
                      width={50}
                    />
                  </TextLink>
                )}
              </div>
              {articleLink && (
                <TextLink
                  className={styles.premiumIntelContainer}
                  href={articleLink}
                  rel="noreferrer"
                  target="_blank"
                  underline="none"
                >
                  Intel
                </TextLink>
              )}
            </div>
          )}
        </div>
      )}
      {showRpmBarrier && (
        <RpmBarrier
          className={styles.rpmBarrier}
          utm_campaign={RpmUtmCampaigns.Player}
        />
      )}
      {(isPick || isConfidenceChange) && (
        <div className={styles.predictorWrapper}>
          <On3Avatar
            altText={`${name} Avatar` || ''}
            className={styles.predictorAvatar}
            domain={expert?.profilePictureResponse?.domain}
            radiusFive
            source={expert?.profilePictureResponse?.source}
          />
          <div className={styles.predictorInfoWrapper}>
            <TextLink
              href={`/user/${expert?.niceName}/predictions/`}
              variant="h5"
            >
              {expert?.name ?? '-'}
            </TextLink>

            {expert?.jobTitle && (
              <Text className={styles.jobTitle}>{expert?.jobTitle}</Text>
            )}
          </div>
          {isPickFinal && correct && (
            <On3IconCheck
              className={clsx(styles.correctPick, styles.mobile)}
              color="inherit"
              height={24}
              width={24}
            />
          )}
          {isPickFinal && !correct && (
            <On3IconX
              className={clsx(styles.incorrectPick, styles.mobile)}
              color="inherit"
              height={24}
              width={24}
            />
          )}
        </div>
      )}

      {hasConfidence && (
        <div className={styles.confidenceContainer}>
          {hasPreviousConfidence && !showFlippedOrg && (
            <div className={styles.confidenceWithTrendContainer}>
              <div className={styles.confidenceDateContainer}>
                <ConfidenceBar
                  confidence={previousConfidence}
                  fade
                  variant="condensedOnly"
                />
                <Text className={styles.dateTime} variant="caption">
                  {previousDate}
                </Text>
              </div>
              <TrendArrow className={clsx(styles[typeOfChangeValue])} />
            </div>
          )}

          <div className={styles.confidenceDateContainer}>
            <ConfidenceBar confidence={confidence} variant="condensedOnly" />
            <Text className={styles.dateTime} variant="caption">
              {predictionDate}
            </Text>
          </div>
        </div>
      )}

      {isNewLeader && (
        <div className={styles.leadersWrapper}>
          <div className={styles.typeContainerDesktop}>
            {typeOfChangeLabel && (
              <Text className={styles.typeOfChangeDesktop} variant="subtitle1">
                {typeOfChangeLabel}
              </Text>
            )}
            <Text className={styles.dateTime} variant="caption">
              {predictionDate}
            </Text>
          </div>
          <div className={styles.newLeaderContainer}>
            <TextLink
              href={`/college/${newLeader?.organization?.slug}/${sluggedSport}/${year}/industry-comparison-commits/`}
              underline="none"
            >
              <On3Avatar
                domain={newLeader?.organization?.asset?.domain}
                height={50}
                source={newLeader?.organization?.asset?.source}
                teamAvatar
                type={newLeader?.organization?.asset?.type}
                width={50}
              />
            </TextLink>
            <div className={styles.currentRank}>
              <Text
                className={styles.rank}
                component="span"
                variant="subtitle1"
              >
                #{newLeader?.currentRank}
              </Text>
              {newLeader?.currentPercentage && (
                <Text className={styles.percent} component="span">
                  {newLeader?.currentPercentage.toFixed(1)}%
                </Text>
              )}
            </div>
          </div>
          <div className={styles.oldLeaderContainer}>
            <Text className={styles.rank} component="span" variant="subtitle1">
              #{oldLeader?.currentRank}
            </Text>
            <TextLink
              href={`/college/${oldLeader?.organization?.slug}/${sluggedSport}/${year}/industry-comparison-commits/`}
              underline="none"
            >
              <On3Avatar
                className={styles.oldLeaderLogo}
                domain={oldLeader?.organization?.asset?.domain}
                height={25}
                source={oldLeader?.organization?.asset?.source}
                teamAvatar
                type={oldLeader?.organization?.asset?.type}
                width={25}
              />
            </TextLink>
            {oldLeader?.currentPercentage && (
              <span className={styles.percent}>
                {oldLeader?.currentPercentage.toFixed(1)}%
              </span>
            )}
          </div>
        </div>
      )}
      {isRankChange && (
        <div className={styles.leadersWrapper}>
          <div className={styles.typeContainerDesktop}>
            {typeOfChangeLabel && (
              <Text className={styles.typeOfChangeDesktop} variant="subtitle1">
                {typeOfChangeLabel}
              </Text>
            )}
            <div className={styles.rankDesktopLogoContainer}>
              <TextLink
                className={styles.rankChangeLogoLinkDesktop}
                href={`/college/${newLeader?.organization?.slug}/${sluggedSport}/${year}/industry-comparison-commits/`}
                underline="none"
              >
                <On3Avatar
                  className={styles.teamLogo}
                  domain={newLeader?.organization?.asset?.domain}
                  height={50}
                  source={newLeader?.organization?.asset?.source}
                  teamAvatar
                  type={newLeader?.organization?.asset?.type}
                  width={50}
                />
              </TextLink>
              <Text className={styles.dateTime} variant="caption">
                {predictionDate}
              </Text>
            </div>
          </div>
          <div className={clsx(styles.newLeaderContainer, styles.rankChange)}>
            <TextLink
              className={styles.rankChangeLogoLinkMobile}
              href={`/college/${newLeader?.organization?.slug}/${sluggedSport}/${year}/industry-comparison-commits/`}
              underline="none"
            >
              <On3Avatar
                domain={newLeader?.organization?.asset?.domain}
                height={40}
                source={newLeader?.organization?.asset?.source}
                teamAvatar
                type={newLeader?.organization?.asset?.type}
                width={40}
              />
            </TextLink>
            <div className={styles.ranksContainer}>
              <div className={styles.oldRank}>
                <Text className={styles.rank} variant="subtitle1">
                  #{newLeader?.previousRank}
                </Text>

                {newLeader?.previousPercentage && (
                  <span className={styles.percent}>
                    {newLeader?.previousPercentage.toFixed(1)}%
                  </span>
                )}
                <TrendArrow className={clsx(styles[rankChange])} />
              </div>
              <div className={styles.currentRank}>
                <Text
                  className={styles.rank}
                  component="span"
                  variant="subtitle1"
                >
                  #{newLeader?.currentRank}
                </Text>
                {newLeader?.currentPercentage && (
                  <Text className={styles.percent} component="span">
                    {newLeader?.currentPercentage.toFixed(1)}%
                  </Text>
                )}
              </div>
            </div>
          </div>
          <div className={styles.oldLeaderContainer}>
            <Text className={styles.rank} component="span" variant="subtitle1">
              #{oldLeader?.currentRank}
            </Text>
            <TextLink
              href={`/college/${oldLeader?.organization?.slug}/${sluggedSport}/${year}/industry-comparison-commits/`}
              underline="none"
            >
              <On3Avatar
                className={styles.oldLeaderLogo}
                domain={oldLeader?.organization?.asset?.domain}
                height={25}
                source={oldLeader?.organization?.asset?.source}
                teamAvatar
                type={oldLeader?.organization?.asset?.type}
                width={25}
              />
            </TextLink>
            {oldLeader?.currentPercentage && (
              <span className={styles.percent}>
                {oldLeader?.currentPercentage.toFixed(1)}%
              </span>
            )}
          </div>
        </div>
      )}
      {isPickFinal && correct && (
        <On3IconCheck
          className={styles.correctPick}
          color="inherit"
          height={24}
          width={24}
        />
      )}
      {isPickFinal && !correct && (
        <On3IconX
          className={styles.incorrectPick}
          color="inherit"
          height={24}
          width={24}
        />
      )}
    </li>
  );
};
